import { Button, FormHelperText, Snackbar, Alert, CircularProgress } from "@mui/material";
import { Add } from "@mui/icons-material";
import { LightingBolt } from "../svgUrls";
import "../styles/runOnDataDialog.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateWorkflow, useGenerateWorkflows } from "../api/workflowsApi";
import ChatInterface from "./ChatInterface";
import { isAuthenticated } from "../utils/auth";
import useAuthCheck from "../hooks/useAuthCheck";

const Dashboard = () => {
    const navigate = useNavigate();
    const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { mutate: createWorkflow } = useCreateWorkflow();
    const { mutate: generateWorkflow } = useGenerateWorkflows();
    
    // Check if user is authenticated
    const isUserAuthenticated = useAuthCheck();

    useEffect(() => {
        const savedWorkflowId = localStorage.getItem('workflowId');
        localStorage.removeItem('workflowId')
        if(savedWorkflowId) {
            navigate(`/workflow/setup/${savedWorkflowId}`);
        }
    }, [navigate]);

    const handleWorkflowReady = (description: string) => {
        // Check authentication first
        if (!isAuthenticated()) {
            setError("You need to be authenticated to create a workflow. Please log in.");
            return;
        }

        // Use the existing generateWorkflow API to create the workflow
        setIsCreatingWorkflow(true);
        setError(null);
        
        // generateWorkflow({ prompt: description }, {
        //     onSuccess: (result) => {
        //         if (result?.id) {
        //             // Navigate directly to the workflow setup page
        //             navigate(`/workflow/setup/${result.id}`);
        //         } else {
        //             setError("Failed to create workflow. Please try again.");
        //         }
        //         setIsCreatingWorkflow(false);
        //     },
        //     onError: (err) => {
        //         console.error("Error creating workflow:", err);
        //         if (err.message && err.message.includes("401")) {
        //             setError("Authentication failed. Please log in again.");
        //         } else {
        //             setError("Failed to create workflow. Please try again.");
        //         }
        //         setIsCreatingWorkflow(false);
        //     }
        // });
        if(description) {
            navigate("/generateWorkflows", {
                state: {
                    generateWorkflowInput: description,
                },
            });
        }
    };

    const handleAddTrigger = () => {
        if (isCreatingWorkflow) return;
        
        // Check authentication first
        if (!isAuthenticated()) {
            setError("You need to be authenticated to create a workflow. Please log in.");
            return;
        }
        
        setIsCreatingWorkflow(true);
        setError(null);
        
        createWorkflow(undefined, {
            onSuccess: (workflow) => {
                setIsCreatingWorkflow(false);
                navigate(`/workflow/setup/${workflow.id}?trigger=true`);
            },
            onError: (err) => {
                console.error("Error creating workflow:", err);
                if (err.message && err.message.includes("401")) {
                    setError("Authentication failed. Please log in again.");
                } else {
                    setError("Failed to create workflow. Please try again.");
                }
                setIsCreatingWorkflow(false);
            }
        });
    };

    const handleCloseError = () => {
        setError(null);
    };

    // If not authenticated, the hook will redirect to login
    if (!isUserAuthenticated) {
        return (
            <div className="flex items-center justify-center h-screen">
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-start min-h-screen w-full py-8">
            <div className="w-[60%] flex-1 flex-col flex justify-center">
                {/* Chat Interface - Takes the first fold */}
                <ChatInterface onWorkflowReady={handleWorkflowReady} />
                
                <div className="relative my-8 w-full">
                    <hr className="horizontalLineAroundOr"/>
                    <div className="orWithLine !bg-backgroundGrey">OR</div>
                </div>

                {/* Add Trigger - Pushed down below the chat interface */}
                <div className="p-3 flex items-center justify-between bg-white w-full border border-solid border-[borderGrey] rounded-lg mb-8"
                    onClick={handleAddTrigger}
                    style={{ cursor: isCreatingWorkflow ? 'default' : 'pointer' }}
                >
                    <div className="flex items-center gap-2">
                        <div className="p-2 border border-solid border-[borderGrey] rounded-lg">
                            <LightingBolt />
                        </div>
                        <div>
                            <div>Add Trigger</div>
                            <FormHelperText>Ex: When a new email is received in Gmail...</FormHelperText>
                        </div>
                    </div>
                    <Button 
                        variant="contained" 
                        className="!bg-nanonetsBlue !capitalize"
                        startIcon={isCreatingWorkflow ? <CircularProgress size={20} color="inherit" /> : <Add />}
                        disabled={isCreatingWorkflow}
                    >
                        {/* {isCreatingWorkflow ? 'Creating...' : 'Trigger'} */}
                        Trigger
                    </Button>
                </div>
            </div>
            
            {/* Error Snackbar */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
            
            {/* Loading Overlay */}
            {/* {isCreatingWorkflow && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px'
                        }}
                    >
                        <CircularProgress />
                        <div>Creating your workflow...</div>
                    </div>
                </div>
            )} */}
        </div>
    );
}

export default Dashboard;